
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { namespace } from 'vuex-class';
import { Client } from '@/types';

import ErrorAlert from '../components/ErrorAlert.vue';
import PasswordField from '../components/PasswordField.vue';
import TextField from '../components/TextField.vue';
import ChangePasswordForm from '../components/ChangePasswordForm.vue';
import CardActions from '../components/CardActions.vue';
import ClientList from '../components/ClientList.vue';

const Auth = namespace('Auth');
const Loader = namespace('Loader');
const Config = namespace('Config');

// eslint-disable-next-line no-shadow
enum Step {
  VerifyLogin,
  SharedAccountUnauthorised,
  ClientConfirmation,
  ChangePassword,
  ChangePasswordSuccess,
}

@Component({
  components: {
    ChangePasswordForm,
    ErrorAlert,
    TextField,
    PasswordField,
    ValidationObserver,
    ValidationProvider,
    CardActions,
    ClientList,
  },
})
export default class ChangePassword extends Vue {
  private readonly Step = Step;

  private stepVal: Step = Step.VerifyLogin; // Step.SharedAccountUnauthorised; // Step.VerifyLogin;

  private username = '';

  private password = '';

  private newPassword = '';

  @Auth.Action
  private readonly login!: (data: {
    usernameOrEmail: string;
    password: string;
  }) => Promise<boolean>;

  @Auth.Action
  private readonly changePassword!: (data: {
    oldPassword: string;
    newPassword: string;
  }) => Promise<boolean>;

  @Auth.Getter
  private readonly clients!: Client[];

  @Auth.Getter
  private readonly isMultipleUser!: boolean;

  @Auth.Getter
  private readonly email!: string;

  @Config.Getter
  private readonly redirectUri!: string | null;

  @Config.Getter('contactUsUrl')
  contactUsUrl!: string;

  @Loader.Action('run')
  private readonly runWithLoader!: <T>(fn: Promise<T>) => Promise<T>;

  mounted(): void {
    this.$router.onReady(() => {
      const { username } = this.$route.query;
      if (username) {
        this.username = username.toString();
      }
    });

    this.$store.dispatch('clearError');
  }

  get step(): Step {
    return this.stepVal;
  }

  set step(value: Step) {
    this.$store.dispatch('clearError');
    this.stepVal = value;
  }

  async handleLogin(): Promise<void> {
    const result = await this.runWithLoader(
      this.login({ usernameOrEmail: this.username, password: this.password }),
    );

    if (result) {
      if (this.isMultipleUser) {
        this.step = Step.SharedAccountUnauthorised;
      } else {
        this.step = this.clients?.length ? Step.ClientConfirmation : Step.ChangePassword;
      }
    }
  }

  goBack(): void {
    this.$router.back();
  }

  async handleChangePassword(): Promise<void> {
    const result = await this.runWithLoader(
      this.changePassword({
        oldPassword: this.password,
        newPassword: this.newPassword,
      }),
    );

    if (result) {
      this.step = Step.ChangePasswordSuccess;
    }
  }

  navigateForgotPassword(): void {
    this.$router.push({
      name: 'forgot-password',
      query: { ...this.$route.query, username: this.email || this.username },
    });
  }

  redirect(): void {
    if (this.redirectUri) {
      window.location.href = this.redirectUri;
    }
  }
}
